import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Brain, 
  TrendingUp, 
  AlertTriangle, 
  Sparkles, 
  RefreshCw,
  Bell,
  ChevronRight,
  Zap,
  Target,
  Shield,
  Clock,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const severityConfig = {
  info: { color: 'bg-blue-500/10 text-blue-500 border-blue-500/30', icon: Brain },
  warning: { color: 'bg-amber-500/10 text-amber-500 border-amber-500/30', icon: AlertTriangle },
  alert: { color: 'bg-red-500/10 text-red-500 border-red-500/30', icon: Bell },
  opportunity: { color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30', icon: Sparkles }
};

const typeConfig = {
  daily_analysis: { label: 'Daily Analysis', icon: TrendingUp },
  trend_alert: { label: 'Trend Alert', icon: TrendingUp },
  change_detection: { label: 'Change Detected', icon: Zap },
  opportunity: { label: 'Opportunity', icon: Target },
  risk_alert: { label: 'Risk Alert', icon: Shield }
};

export const AIInsightsPanel = () => {
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const fetchInsights = useCallback(async () => {
    try {
      const [insightsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/ai/insights?limit=50`),
        axios.get(`${API}/ai/insights/summary`)
      ]);
      
      setInsights(insightsRes.data.insights || []);
      setSummary(summaryRes.data.summary || null);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/ai/analyze`);
      const result = response.data;
      
      toast.success(`Generated ${result.insights_generated} new insights`);
      
      // Refresh insights
      await fetchInsights();
    } catch (err) {
      toast.error('Analysis failed. Please try again.');
      console.error('Analysis error:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const markAsRead = async (insightId) => {
    try {
      await axios.post(`${API}/ai/insights/${insightId}/read`);
      setInsights(prev => prev.map(i => 
        i.id === insightId ? { ...i, read: true } : i
      ));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const filteredInsights = activeTab === 'all' 
    ? insights 
    : insights.filter(i => i.insight_type === activeTab);

  const unreadCount = insights.filter(i => !i.read).length;

  if (loading) {
    return (
      <Card className="border-border">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border" data-testid="ai-insights-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Brain className="w-5 h-5 text-accent" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                AI Intelligence Center
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="bg-accent text-accent-foreground">
                    {unreadCount} new
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Autonomous insights from continuous market analysis
              </CardDescription>
            </div>
          </div>
          <Button 
            onClick={runAnalysis} 
            disabled={analyzing}
            size="sm"
            className="gap-2"
            data-testid="run-analysis-btn"
          >
            {analyzing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Run Analysis
              </>
            )}
          </Button>
        </div>

        {/* Summary Stats */}
        {summary && summary.total > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {summary.by_severity?.opportunity > 0 && (
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
                <Sparkles className="w-3 h-3 mr-1" />
                {summary.by_severity.opportunity} Opportunities
              </Badge>
            )}
            {summary.by_severity?.warning > 0 && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {summary.by_severity.warning} Warnings
              </Badge>
            )}
            {summary.by_severity?.alert > 0 && (
              <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
                <Bell className="w-3 h-3 mr-1" />
                {summary.by_severity.alert} Alerts
              </Badge>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start mb-4 bg-muted/50">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="daily_analysis" className="text-xs">Daily</TabsTrigger>
            <TabsTrigger value="opportunity" className="text-xs">Opportunities</TabsTrigger>
            <TabsTrigger value="change_detection" className="text-xs">Changes</TabsTrigger>
            <TabsTrigger value="risk_alert" className="text-xs">Risks</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-0">
            {filteredInsights.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No insights yet</p>
                <p className="text-sm">Click "Run Analysis" to generate AI insights</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {filteredInsights.map((insight) => {
                    const severity = severityConfig[insight.severity] || severityConfig.info;
                    const type = typeConfig[insight.insight_type] || typeConfig.daily_analysis;
                    const SeverityIcon = severity.icon;
                    const TypeIcon = type.icon;
                    
                    return (
                      <div
                        key={insight.id}
                        className={`p-4 rounded-lg border transition-all cursor-pointer hover:bg-muted/50 ${
                          insight.read ? 'opacity-70' : ''
                        } ${severity.color}`}
                        onClick={() => !insight.read && markAsRead(insight.id)}
                        data-testid={`insight-${insight.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <SeverityIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h4 className="font-semibold text-sm">{insight.title}</h4>
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                  <TypeIcon className="w-3 h-3 mr-1" />
                                  {type.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                                {insight.content}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(insight.created_at).toLocaleString()}
                                </span>
                                {insight.games_affected?.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Target className="w-3 h-3" />
                                    {insight.games_affected.length} games
                                  </span>
                                )}
                                {insight.read && (
                                  <span className="flex items-center gap-1 text-emerald-500">
                                    <CheckCircle className="w-3 h-3" />
                                    Read
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 opacity-50 flex-shrink-0" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AIInsightsPanel;
